<template>
  <div class="dropzone" :class="{ '-unique': unique }">
    <div
      class="dropzone__container"
      :class="{ '-dragging': isDragging, '-disabled': isDisabled }"
      @dragover="dragover"
      @dragleave="dragleave"
      @drop="drop"
    >
      <input
        type="file"
        multiple
        name="file"
        :id="'fileInput-' + inputName"
        class="dropzone__hidden"
        @change="onChange"
        ref="file"
        :accept="computedAccept"
      />

      <label v-if="!loading" :for="'fileInput-' + inputName" class="dropzone__label">
        <div v-if="isDragging">
          <div class="dropzone__cloud"><FontAwesomeIcon icon="fa-light fa-download" /></div>
          {{ $t("dropzone.release") }}
        </div>
        <div :class="{ 'd-none': unique && files.length }" v-else>
          <div class="dropzone__cloud">
            <FontAwesomeIcon icon="fa-light fa-cloud-upload" />
          </div>
          {{ $t("dropzone.drop") }} <u>{{ $t("dropzone.click_here") }}</u> {{ $t("dropzone.to_upload") }}
          {{ $t("dropzone.max_files", { count: this.maxFiles }) }}
        </div>
      </label>
      <div v-if="loading" class="dropzone__progress">
        <div class="dropzone__progress__label">{{ $t("dropzone.downloading") }}</div>
        <div class="dropzone__progress__bar"></div>
        <div class="dropzone__progress__count">{{ $t("dropzone.file") }} {{ filesUploaded }}/{{ filesToUpload }}</div>
      </div>
    </div>
    <div class="dropzone__preview" :class="{ '-unique': unique }" v-if="files.length">
      <div
        v-for="file in files"
        :key="file.id"
        class="dropzone__file"
        v-hide="{ hide: !!file.removed }"
        @hideComplete="hideComplete(file)"
      >
        <div class="d-flex align-items-center">
          <img class="dropzone__file__img" v-if="isPicture(file)" :src="generateThumbnail(file)" />
          <div v-else class="dropzone__file__icon">
            <FontAwesomeIcon v-if="isPdf(file)" icon="fa-light fa-file-pdf" />
            <FontAwesomeIcon v-if="isWord(file)" icon="fa-light fa-file-doc" />
            <FontAwesomeIcon v-if="isExcel(file)" icon="fa-light fa-file-xls" />
          </div>
          <p class="dropzone__file__name" :title="file.originalName">
            {{ file.originalName }}
          </p>
        </div>
        <div class="d-flex align-item-center">
          <button
            v-tooltip="{ title: this.$t('download_file') }"
            class="ml-2 dropzone__file__download"
            @click="download(file)"
          >
            <FontAwesomeIcon icon="fa-light fa-download" />
          </button>
          <button
            v-if="!file.loading"
            class="ml-2 dropzone__file__remove"
            type="button"
            @click="askRemove(file)"
            v-tooltip="{ title: stringManager().globalTranslation('delete', 'file', 'studea_manager', 1, true) }"
          >
            <FontAwesomeIcon icon="fa-light fa-xmark" />
          </button>
          <div v-else class="dropzone__file__loading">
            <div class="spinner-border text-primary align-self-center loader"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { fileManager, NATIVE_APP_FILE_ACCEPT } from "@/services/file/file-manager";
import { apiConnection } from "@/services/api-connection";
import { pictureManager } from "@/services/utilities/picture-manager";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { formValidator } from "@/services/form/form-validator";
import { stringManager } from "@/services/utilities/string-manager";
import { Capacitor } from "@capacitor/core";

const UPLOAD_TIMEOUT_MS = 60000;

export default {
  name: "FileDropzoneTypeComponent",
  components: { FontAwesomeIcon },
  props: {
    existingFiles: {
      type: Array,
      required: false,
      default: () => [],
    },
    confName: {
      type: String,
      required: true,
    },
    confPath: {
      type: String,
      required: true,
    },
    confSubject: {
      type: String,
      required: true,
    },
    maxFiles: {
      type: Number,
      required: false,
      default: 5,
    },
    maxFileSize: {
      type: Number,
      required: false,
      default: 5,
    },
    extensionsAccepted: {
      type: RegExp,
      required: false,
      default: () => /^([./])(webp|gif|pdf|doc|docx|xls|xlsx|jpe?g|png|svg\+xml|bmp|csv|msword)$/,
    },
    extensionsAcceptedMessage: {
      type: String,
      required: false,
      default: () => i18n.global.t("extensions_accepted"),
    },
    unique: {
      type: Boolean,
      required: false,
      default: false,
    },
    inputName: {
      type: String,
      required: false,
      default: "dropzone",
    },
    thumbnail: {
      type: String,
      required: false,
      default: "dropzone",
    },
  },
  emits: ["uploading", "uploaded", "askRemove"],
  data() {
    return {
      files: this.existingFiles,
      isDragging: false,
      loading: false,
      filesToUpload: 0,
      filesUploaded: 0,
      error: "",
      stopUpload: false,
      isExtensionError: false,
      isMaxSizeError: false,
      isFileLimitError: false,
      isTimeoutError: false,
      isVideoError: false,
      isNativeApp: Capacitor.getPlatform() !== "web",
    };
  },
  computed: {
    isDisabled() {
      return this.files.length >= this.maxFiles;
    },

    hasError() {
      return (
        this.isMaxSizeError ||
        this.isExtensionError ||
        this.isFileLimitError ||
        this.isTimeoutError ||
        this.isVideoError
      );
    },

    computedAccept() {
      return this.isNativeApp ? NATIVE_APP_FILE_ACCEPT : this.extensionsAccepted;
    },
  },
  watch: {
    existingFiles() {
      this.files = this.existingFiles;
    },
  },
  methods: {
    stringManager() {
      return stringManager;
    },
    async onChange() {
      this.error = "";
      this.isMaxSizeError = false;
      this.isExtensionError = false;
      this.isFileLimitError = false;
      this.isTimeoutError = false;
      this.isVideoError = false;
      this.loading = true;
      this.filesToUpload = this.$refs.file.files.length;
      this.filesUploaded = 0;
      try {
        for (let i = 0; i < this.filesToUpload; i++) {
          if (this.validFile(this.$refs.file.files[i]) && !this.stopUpload) {
            await this.upload(this.$refs.file.files[i]);
          }
        }
      } finally {
        this.loading = false;
      }
      if (this.hasError) {
        this.showErrors();
      }

      this.$emit("uploaded", { files: this.files, count: this.filesUploaded, error: this.hasError });
    },

    generateThumbnail(file) {
      return pictureManager.setPicture(file, this.thumbnail);
    },

    showErrors() {
      if (this.isExtensionError || this.isMaxSizeError) {
        this.error = this.error.concat(this.error !== "" ? "</ul>" : "");
      }
      if (this.isExtensionError) {
        this.error = this.error.concat("<p>");
        this.error = this.error.concat(this.extensionsAcceptedMessage);
        this.error = this.error.concat("</p>");
      }
      if (this.isMaxSizeError) {
        this.error = this.error.concat("<p>");
        this.error = this.error.concat(i18n.global.tc("dropzone.file_size_error", { size: this.maxFileSize }));
        this.error = this.error.concat("</p>");
      }
      if (this.isFileLimitError) {
        this.error = this.error.concat("<p>");
        this.error = this.error.concat(i18n.global.t("dropzone.max_files_message"));
        this.error = this.error.concat("</p>");
      }
      if (this.isTimeoutError) {
        this.error = this.error.concat("<p>");
        this.error = this.error.concat(i18n.global.t("dropzone.upload_timeout_error"));
        this.error = this.error.concat("</p>");
      }
      if (this.isVideoError) {
        this.error = this.error.concat("<p>");
        this.error = this.error.concat(i18n.global.t("video_not_supported_mobile"));
        this.error = this.error.concat("</p>");
      }

      // ST143-TI-17771 (§2.7, SweetAlert2 html:true): this.error concatenates <p>/<ul>/<li> wrappers around
      // i18n strings, but also interpolates the user-selected file's name (dropzone.max_file_size /
      // dropzone.file_extension_error, above) — a filename is attacker-controlled. Sanitized centrally in
      // notificationManager.showAlert().
      notificationManager.showAlert(
        "warning",
        i18n.global.t("dropzone.error_title"),
        this.error,
        true,
        false,
        i18n.global.t("global.close"),
        null,
        "#000",
        "",
        true,
      );
    },

    validFile(file) {
      if (this.files.length >= this.maxFiles) {
        this.isFileLimitError = true;
        return false;
      } else if (this.isNativeApp && this.isVideo(file)) {
        this.isVideoError = true;
        return false;
      } else if (!this.validSize(file.size)) {
        this.error = this.error.concat(this.error === "" ? "<ul>" : "");
        this.error = this.error.concat("<li>");
        this.error = this.error.concat(i18n.global.tc("dropzone.max_file_size", { name: file.name }));
        this.error = this.error.concat("</li>");
        this.isMaxSizeError = true;
        return false;
      } else if (!this.validExtension(file.name)) {
        this.error = this.error.concat(this.error === "" ? "<ul>" : "");
        this.error = this.error.concat("<li>");
        this.error = this.error.concat(i18n.global.tc("dropzone.file_extension_error", { name: file.name }));
        this.error = this.error.concat("</li>");
        this.isExtensionError = true;
        return false;
      }
      return true;
    },

    validExtension(filename) {
      return formValidator.validFileExtension(filename, this.extensionsAccepted);
    },

    validSize(size) {
      return formValidator.validFileSize(size, this.maxFileSize);
    },

    isPicture(file) {
      return fileManager.isPicture(file);
    },

    isPdf(file) {
      return fileManager.isPdf(file);
    },

    isWord(file) {
      return fileManager.isWord(file);
    },

    isExcel(file) {
      return fileManager.isExcel(file);
    },

    isVideo(file) {
      return fileManager.isVideo(file);
    },

    async upload(file) {
      const result = await fileManager.fileToB64(file);
      const data = {
        data: result.split(",")[1],
        originalName: file.name,
        conf: this.confName,
        path: this.confPath,
        subject: this.confSubject,
      };
      this.$emit("uploading");
      let res;
      try {
        res = await apiConnection.post("/app/file/upload", data, true, true, UPLOAD_TIMEOUT_MS);
      } catch (error) {
        if (error && error.isTimeout) {
          this.isTimeoutError = true;
          this.filesUploaded++;
          return false;
        }
        throw error;
      }

      if (!res.hasError) {
        file = { ...file, ...res.responses[0].object };
        this.files.push(file);
      }
      this.filesUploaded++;
      return !res.hasError;
    },

    askRemove(file) {
      file.loading = true;
      this.$emit("askRemove", file);
    },

    removeConfirmation(file) {
      file.removed = true;
    },

    hideComplete(file) {
      const i = this.files.indexOf(file);
      this.files.splice(i, 1);
      file.loading = false;
    },

    cancelRemove(file) {
      file.loading = false;
    },

    dragover(e) {
      e.preventDefault();
      if (this.isDisabled) return;
      this.isDragging = true;
    },

    dragleave() {
      if (this.isDisabled) return;
      this.isDragging = false;
    },

    drop(e) {
      e.preventDefault();
      if (this.isDisabled) {
        notificationManager.showNotification("error", i18n.global.t("dropzone.max_files_message"));
      } else {
        this.$refs.file.files = e.dataTransfer.files;
        this.onChange();
      }
      this.isDragging = false;
    },

    async download(file) {
      if (file) {
        let a = document.createElement("a");
        a.href = fileManager.getDownloadSrc(file);
        a.download = file.originalName;
        a.click();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.dropzone {
  position: relative;
  display: flex;
  text-align: center;
  justify-content: space-between;

  &.-unique {
    flex-direction: column;
  }
  @include up-to-md {
    flex-direction: column;
  }

  &__container {
    padding: 1.5rem;
    background: $lighter-grey;
    border: 1px dashed $m_color_6;
    border-radius: $radius;
    width: 100%;
    position: relative;
    min-height: 122px;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;

    &.-disabled {
      &:after {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        content: "";
        background: rgba(255, 255, 255, 0.5);
        z-index: 3;
        cursor: not-allowed;

        @include dark-theme {
          background: $m-color_12;
        }
      }
    }

    @include dark-theme {
      background: $dark-theme-bg;
    }

    &.-dragging {
      border: 2px dashed $green;
      background: $light-green;

      .dropzone__label {
        color: $green !important;
      }
    }
  }

  &__hidden {
    opacity: 0;
    overflow: hidden;
    position: absolute;
    width: 1px;
    height: 1px;
  }

  &__label {
    font-size: 16px;
    display: block;
    cursor: pointer;
  }

  &__preview {
    width: 50%;
    min-width: 50%;
    height: 100%;
    padding-left: 15px;

    &.-unique {
      z-index: 3;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 80%;
      height: fit-content;
      padding: 8px;
      border-radius: $radius;
      border: 1px solid #e0e6ed;
      box-shadow: 0 0 40px 0 rgba(94, 92, 154, 0.06);
      .dropzone__file {
        padding-right: 0;
      }
    }

    @include up-to-md {
      width: 100%;
      padding-left: 0;
      margin-top: 15px;
    }
  }

  &__file {
    display: flex;
    justify-content: space-between;
    padding: 5px 35px 5px 0;
    margin-left: 5px;
    align-items: center;
    position: relative;

    &__name {
      max-width: 180px;
      margin: 0 !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      flex-grow: 1;
      text-align: left !important;
    }

    &__img,
    &__icon {
      width: 35px;
      height: 35px;
      object-fit: cover;
      border-radius: 5px;
      border: 1px dashed $m_color_6;
      margin-right: 10px;
    }

    &__icon {
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }

    &__remove,
    &__download {
      background: none;
      border: none;
      width: 25px;
      height: 25px;
      font-size: 18px;
    }

    &__download {
      color: $black;
    }

    &__remove {
      color: $red;
    }

    &__loading {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 20px;
      height: 20px;

      .loader {
        width: 20px;
        height: 20px;
      }
    }
  }

  &__file + &__file {
    border-top: 1px dashed $m_color_6;
  }

  &__cloud {
    font-size: 25px;
  }

  &__progress {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 50%;
    transform: translate(-50%, -50%);

    &__label {
      font-weight: bold;
      margin-bottom: 5px;
    }

    &__count {
      margin-top: 5px;
    }

    &__bar {
      width: 100%;
      height: 10px;
      border-radius: 10px;
      background: $light-blue;
      overflow: hidden;
      position: relative;

      &:after {
        content: "";
        width: 20%;
        background: $blue;
        position: absolute;
        left: 0;
        top: 0;
        height: 10px;
        animation: progress-bar 1500ms ease-in-out infinite;
      }
    }
  }
}

@keyframes progress-bar {
  0% {
    left: -100%;
    width: 10%;
  }
  50% {
    width: 50%;
  }
  100% {
    left: 100%;
    width: 10%;
  }
}
</style>
